import * as THREE from 'three';
import type { CharacterLook } from '../state/types';
import { buildCharacter } from './builders/character';

/** What a portrait shows: the head (the default), or a closer look at part of the outfit. */
export type PortraitFrame = 'head' | 'torso' | 'legs' | 'feet';

/** Camera height, aim and distance for each framing (scaled by the character's height). */
const FRAMES: Record<PortraitFrame, { y: number; at: number; dist: number; side: number; turn: number }> = {
  head: { y: 1.78, at: 1.62, dist: 1.55, side: 0.18, turn: -0.35 },
  torso: { y: 1.36, at: 1.2, dist: 2.2, side: 0.25, turn: -0.35 },
  legs: { y: 0.8, at: 0.6, dist: 2.3, side: 0.3, turn: -0.45 },
  feet: { y: 0.45, at: 0.13, dist: 1.25, side: 0.35, turn: -0.7 },
};

/**
 * Renders a portrait of a character model (head and shoulders, unless `frame` says
 * otherwise) to a PNG data URL, using the main renderer and an offscreen render target.
 */
export function renderPortrait(
  renderer: THREE.WebGLRenderer,
  look: CharacterLook,
  size = 192,
  frame: PortraitFrame = 'head',
): string {
  const f = FRAMES[frame];
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight('#ffffff', '#6b5a4a', 1.6));
  const key = new THREE.DirectionalLight('#fff4e0', 2.4);
  key.position.set(1.5, 3, 3);
  scene.add(key);
  const rig = buildCharacter(look);
  rig.root.rotation.y = f.turn;
  scene.add(rig.root);

  const h = look.height;
  const camera = new THREE.PerspectiveCamera(26, 1, 0.1, 20);
  // Hats stick up: the head shot backs off a touch for them.
  const hat = frame === 'head' && look.hat !== 'none' ? 1.08 : 1;
  camera.position.set(f.side * h, (f.y + (hat - 1) * 0.4) * h, f.dist * h * hat);
  camera.lookAt(0, (f.at + (hat - 1) * 0.6) * h, 0);

  const rt = new THREE.WebGLRenderTarget(size, size);
  rt.texture.colorSpace = THREE.SRGBColorSpace;
  const prevTarget = renderer.getRenderTarget();
  const prevClear = renderer.getClearColor(new THREE.Color());
  const prevAlpha = renderer.getClearAlpha();
  renderer.setRenderTarget(rt);
  renderer.setClearColor(0x000000, 0);
  renderer.clear();
  renderer.render(scene, camera);
  const pixels = new Uint8Array(size * size * 4);
  renderer.readRenderTargetPixels(rt, 0, 0, size, size, pixels);
  renderer.setRenderTarget(prevTarget);
  renderer.setClearColor(prevClear, prevAlpha);

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(size, size);
  // WebGL rows are bottom-up; flip while copying.
  for (let y = 0; y < size; y++) {
    const src = (size - 1 - y) * size * 4;
    img.data.set(pixels.subarray(src, src + size * 4), y * size * 4);
  }
  ctx.putImageData(img, 0, 0);

  rt.dispose();
  rig.root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) m.geometry.dispose();
  });
  return canvas.toDataURL('image/png');
}
